import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';

import styles from './BreedingPage.module.scss';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../Auth';
import PageTable from '../../Shared/PageTable/PageTable';
import { general } from '../../../general';
import { UserSelectCell, useBreedUsers, userDisplayName, TextCell } from '../../Shared/SchemaFieldCells/SchemaFieldCells';

function BreedingPage(props: BreedingPageProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const params = useParams();

    // Samme mønster som Parringer: fanen ligger i URL-en så den overlever
    // tilbakenavigering, med replace: true så fanebytter ikke fyller historikken.
    const tabKeys = ["new", "accepted", "rejected"];
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get("tab");
    const activeTab = tabParam != null && tabKeys.includes(tabParam) ? tabParam : "new";

    function setActiveTab(key: string) {
        const params = new URLSearchParams(searchParams);
        params.set("tab", key);
        setSearchParams(params, { replace: true });
    }

    // edit: "user" er nedtrekk fra brukerlisten (lagrer bruker-ID, med gammel
    // fritekst i legacyField), "text" er fritt tekstfelt.
    const fieldsInTable = [
        { field: "TIMESTAMP" },
        { field: "DOGNAME" },
        { field: "SEX" },
        { field: "OWNERNAME" },
        { field: "POSTCODE" },
        { field: "RESPONSIBLE_USER_ID", edit: "user", legacyField: "RESPONSIBLE" },
        { field: "CONTROLLER_USER_ID", edit: "user", legacyField: "CONTROLLER" },
        { field: "PROCESS_STATUS", edit: "text" },
    ];

    const breedUsers = useBreedUsers();

    function createTraitColumn(trait: any) {
        if (trait != undefined) {
            let column: any = {
                label: t("breedingpage.fields." + trait.field),
                field: trait.field,
                //noFilter: true,
            };

            if (trait.field == "TIMESTAMP") {
                column["override"] = (data: any) => {
                    return new Date(data.TIMESTAMP).toLocaleString("no-NO");
                }
            }

            if (trait.field == "SEX") {
                column["override"] = (data: any) => {
                    return t("breedingpage.fields." + data.SEX);
                }
                column["customFilters"] = [
                    { label: t("breedingpage.fields.male"), value: "male", },
                    { label: t("breedingpage.fields.female"), value: "female", },
                ];
            }
            
            if (trait.edit == "user") {
                column["override"] = (data: any) => (
                    <UserSelectCell row={data} field={trait.field} legacyField={trait.legacyField} users={breedUsers} />
                );
                // Kolonnen holder en bruker-ID, så fritekstfilteret gir ingen
                // mening. Filtrer på valgt bruker i stedet. Skjemaer som bare
                // har gammel fritekst treffes ikke.
                column["customFilters"] = breedUsers.map((user: any) => ({
                    label: userDisplayName(user),
                    value: String(user.ID),
                }));
            }

            if (trait.edit == "text") {
                column["override"] = (data: any) => (
                    <TextCell row={data} field={trait.field} label={t("breedingpage.fields." + trait.field)} />
                );
            }

            return column;
        } else {
            return undefined;
        }
    }

    function createColumns() {
        let columns: any[] = [];
        fieldsInTable.forEach((trait: object) => {
            columns.push(createTraitColumn(trait));
        });
        
        return columns;
    }

    useEffect(() => {
        if (
            (
                auth.user?.userdata?.ROLE != "admin" &&
                auth.user?.userdata?.ROLE != "sysadmin" &&
                auth.user?.userdata?.ROLE != "user"
            ) ||
            general.getBreedAccess(auth.user, auth.selectedBreed) == "dogowner"
        ) {
            navigate("/");
        }
    }, [auth.user?.userdata?.ROLE, auth.selectedBreed]);

    return (
        <div className="maincontent">
            <div className={styles.dogs}>
                <h1>{t("breedingpage.title")}</h1>
                <div
                    className={styles.configbutton}
                >
                    <button
                        onClick={() => {
                            navigate("/avlshundsoknader/config");
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-gear-fill" viewBox="0 0 16 16">
                            <path d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311c.446.82.023 1.841-.872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1 .872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872-2.105l.34-.1c1.4-.413 1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872zM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858z"/>
                        </svg>
                    </button>
                </div>
                <div className={styles.tabs}>
                    <div className={styles.tabList}>
                        {[
                            { key: "new",      label: t("breedingpage.filters.status.new") },
                            { key: "accepted", label: t("breedingpage.filters.status.accepted") },
                            { key: "rejected", label: t("breedingpage.filters.status.rejected") },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tabUnderline} />
                </div>
                { auth.selectedBreed != undefined &&
                    <PageTable
                        dataUrl={"/api/schema/getAllPaged/" + auth.selectedBreed}
                        // Radene har redigerbare celler, så de må nøkles på skjema-ID —
                        // ellers beholder inputene forrige fanes verdier (se PageTable).
                        rowKey={(dataLine: any) => dataLine.ID}
                        saveFiltersKey={"breedingpage.table." + auth.selectedBreed}
                        onLineClick={(dataLine: any) => {
                            // Ta med fanen inn i detaljsida, så tilbake-knappen der
                            // treffer riktig fane.
                            navigate("/avlshundsoknader/" + dataLine.SCHEMA_KEY + "/" + dataLine.ID, {
                                state: { listSearch: location.search },
                            });
                        }}
                        options={{
                            showFields: [
                                ...createColumns(),
                            ],
                        }}
                        permaFilters={[
                            {
                                field: "BREED_ID",
                                exact: false,
                                type: "and",
                                values: [auth.selectedBreed?.toString() || ""]
                            },
                            {
                                field: "STATUS",
                                exact: false,
                                type: "and",
                                values: [activeTab]
                            },
                            {
                                field: "SCHEMA_KEY",
                                exact: false,
                                type: "and",
                                values: ["breedreg"]
                            }
                        ]}
                        selectedTraits={fieldsInTable}
                        clickable
                        defaultSortDirection="DESC"
                        defaultSortField="TIMESTAMP"
                        hidePagination={false}
                        hidePagesize={false}
                        hideFilter={false}
                    />
                }
            </div>
        </div>
    );
}

class BreedingPageProps {
    
}

export default BreedingPage;